import { useMemo, useRef, useState, useEffect } from "react";
import { ProTable, ActionType, ProColumns } from "@ant-design/pro-components";
import { Form } from "antd";
import { createButtons } from "action-view";

import { SearchForm } from "./SearchForm";
import { createActionColumn, createRequestFunction, createProTableTabs } from "./utils";

import type { TTabsProps, TRecord, TTableProps, TRequestFunc } from "../../dataTypes";

export function SearchableTable<
  TItem extends TRecord,
  TFilterShape extends TRecord = TRecord,
  TTabFilterShape extends TRecord = TRecord,
  TApiVariableShape extends TRecord = TRecord,
>({
  columns,
  onList,
  tableId,
  tableTitle,
  pageActions,
  buildRowActions,
  searchConfig,
  filters,
  tabs = [],
  defaultTabKey,
  buildApiPayload,
  rowKey = "id",
  pullInterval = 0,
  maxVisibleRowActions = 2,
  reloadOnDataChange = false,
}: TTableProps<TItem, TFilterShape, TTabFilterShape, TApiVariableShape>) {
  const [form] = Form.useForm();
  const actionRef = useRef<ActionType>();
  const [activeTabKey, setActiveTabKey] = useState<React.Key>(defaultTabKey as React.Key);
  const [searchParams, setSearchParams] = useState<TFilterShape>();

  const tabFilter = useMemo<TTabFilterShape>(() => {
    const current = tabs.find((tab) => tab.key === activeTabKey);
    return (current?.filter ?? {}) as TTabFilterShape;
  }, [activeTabKey, tabs]);

  const request: TRequestFunc<TItem> = useMemo(
    () => createRequestFunction(onList, searchParams, tabFilter, buildApiPayload),
    [onList, searchParams, tabFilter, buildApiPayload],
  );

  useEffect(() => {
    if (pullInterval > 0) {
      const timer = setInterval(() => {
        actionRef.current?.reload();
      }, pullInterval);
      return () => clearInterval(timer);
    }
  }, [pullInterval]);

  useEffect(() => {
    if (reloadOnDataChange) {
      actionRef.current?.reload();
    }
  }, [searchParams, activeTabKey, reloadOnDataChange]);

  const onSearch = async () => {
    const values = await form.validateFields();
    console.log("onSearch", values);
    setSearchParams(values);
    actionRef.current?.reload();
  };

  const handleReset = () => {
    form.resetFields();
    setSearchParams({} as TFilterShape);
    actionRef.current?.reload();
  };

  const allColumns: ProColumns<TItem>[] = useMemo(() => {
    const actionColumn = createActionColumn(buildRowActions, maxVisibleRowActions);
    return actionColumn ? [...columns, actionColumn] : columns;
  }, [columns, buildRowActions, maxVisibleRowActions]);

  const onTabChange = (newKey: React.Key) => {
    console.log("onTabChange:", newKey);
    setActiveTabKey(newKey);
    onSearch();
  };

  const toolbar: TTabsProps = createProTableTabs({
    tabs,
    activeKey: activeTabKey,
    onTabChange,
  });

  return (
    <div>
      {tableTitle && <h3 style={{ marginBottom: 16 }}>{tableTitle}</h3>}

      {filters && (
        <SearchForm
          form={form}
          filters={filters}
          onSearch={onSearch}
          onReset={handleReset}
          searchConfig={searchConfig}
        />
      )}

      <ProTable<TItem>
        request={request}
        columns={allColumns}
        rowKey={rowKey}
        actionRef={actionRef}
        toolBarRender={() => createButtons(pageActions || [])}
        toolbar={toolbar}
        columnsState={{
          persistenceKey: `${tableId || tableTitle}-column-state`,
          persistenceType: "localStorage" as const,
        }}
        search={false}
        pagination={{ showSizeChanger: true }}
        scroll={{ x: "max-content" }}
      />
    </div>
  );
}
